import { httpError } from './errors.js'
import { emailDomain } from './email.js'

/**
 * Operator catalogue. `kinds` drives which operators the UI offers for a column,
 * `arity` how many value inputs it renders. Kept here so client and server agree.
 */
export const OPERATORS = [
  { id: 'contains', label: 'contains', kinds: ['text'], arity: 1 },
  { id: 'not_contains', label: 'does not contain', kinds: ['text'], arity: 1 },
  { id: 'equals', label: 'equals', kinds: ['text', 'number'], arity: 1 },
  { id: 'not_equals', label: 'does not equal', kinds: ['text', 'number'], arity: 1 },
  { id: 'starts_with', label: 'starts with', kinds: ['text'], arity: 1 },
  { id: 'ends_with', label: 'ends with', kinds: ['text'], arity: 1 },
  { id: 'in', label: 'is any of', kinds: ['text', 'number'], arity: 'list' },
  { id: 'not_in', label: 'is none of', kinds: ['text', 'number'], arity: 'list' },
  { id: 'regex', label: 'matches regex', kinds: ['text'], arity: 1 },
  { id: 'gt', label: '>', kinds: ['number'], arity: 1 },
  { id: 'gte', label: '>=', kinds: ['number'], arity: 1 },
  { id: 'lt', label: '<', kinds: ['number'], arity: 1 },
  { id: 'lte', label: '<=', kinds: ['number'], arity: 1 },
  { id: 'between', label: 'between', kinds: ['number'], arity: 2 },
  { id: 'is_empty', label: 'is empty', kinds: ['text', 'number'], arity: 0 },
  { id: 'is_not_empty', label: 'is not empty', kinds: ['text', 'number'], arity: 0 },
  // These two need the Emails tool's domain check to have run on the column
  // first — `needs` is how the UI knows to say so.
  { id: 'email_valid', label: 'is a valid email', kinds: ['text'], arity: 0, needs: 'email-check' },
  { id: 'email_invalid', label: 'is a broken email', kinds: ['text'], arity: 0, needs: 'email-check' },
]

const EMAIL_OPS = new Set(['email_valid', 'email_invalid'])

const OP_IDS = new Set(OPERATORS.map((o) => o.id))

/**
 * A filter spec is a tree. Both kinds of node live in the same `rules` array:
 *
 *   group: { combinator: 'AND' | 'OR', rules: [node, …] }
 *   rule:  { column, op, value?, value2?, values?, caseSensitive? }
 *
 * A node is a group when it has a `rules` array, which is how `A and (B or C)`
 * is expressed. A flat spec — one group of rules, no nesting — is the same shape
 * it has always been, so older saved views keep working unchanged.
 */
export const MAX_DEPTH = 8
export const MAX_RULES = 200

export const isGroup = (node) => !!node && Array.isArray(node.rules)

const combinatorOf = (node) => (node.combinator === 'OR' ? 'OR' : 'AND')

/**
 * Drop the nodes that cannot narrow anything — half-built rules and empty
 * groups — and unwrap one-child groups, so `(A)` and `A` behave (and cache) the
 * same. Returns null when nothing filterable is left.
 *
 * @returns {null | object} the surviving node; leaf rules are returned as-is
 */
export function pruneSpec(node, depth = 0, counter = { n: 0 }) {
  if (depth > MAX_DEPTH) throw httpError(400, `Filter groups are nested more than ${MAX_DEPTH} deep.`)
  if (isGroup(node)) {
    const rules = []
    for (const child of node.rules) {
      const kept = pruneSpec(child, depth + 1, counter)
      if (kept) rules.push(kept)
    }
    if (rules.length === 0) return null
    if (rules.length === 1) return rules[0]
    return { combinator: combinatorOf(node), rules }
  }
  if (!node || !node.column || !node.op) return null
  if (++counter.n > MAX_RULES) throw httpError(400, `A filter can have at most ${MAX_RULES} rules.`)
  return node
}

/** Count the rules (not groups) a spec would actually apply. */
export function countRules(node) {
  if (!node) return 0
  if (isGroup(node)) return node.rules.reduce((sum, child) => sum + countRules(child), 0)
  return node.column && node.op ? 1 : 0
}

function keyNode(node) {
  if (isGroup(node)) return [combinatorOf(node), node.rules.map(keyNode)]
  return [
    node.column,
    node.op,
    node.value ?? '',
    node.value2 ?? '',
    Array.isArray(node.values) ? [...node.values].sort() : [],
    !!node.caseSensitive,
  ]
}

/** The columns the spec applies an email operator to. */
function emailColumns(node, out = new Set()) {
  if (isGroup(node)) node.rules.forEach((child) => emailColumns(child, out))
  else if (EMAIL_OPS.has(node.op)) out.add(node.column)
  return out
}

/**
 * Stable cache key so identical filter sets reuse a running/finished scan.
 *
 * Pass the dataset when the spec may use email operators: their verdicts come
 * from that dataset's domain check, so the check's version is part of what the
 * filter *is* — re-running the check must not reuse a scan built on the old one.
 */
export function filterKey(spec, ds = null) {
  const pruned = pruneSpec(spec)
  if (!pruned) return ''
  let key = JSON.stringify(keyNode(pruned))
  if (ds) {
    const versions = [...emailColumns(pruned)]
      .sort()
      .map((col) => `${col}:${ds.emailChecks?.get(col)?.version ?? 0}`)
    if (versions.length > 0) key += `||email:${versions.join(',')}`
  }
  return key
}

function toNumber(s) {
  if (s === '') return NaN
  return Number(s)
}

function compileRule(rule, headers, ds) {
  const col = headers.indexOf(rule.column)
  if (col < 0) throw httpError(400, `Unknown column: ${rule.column}`)
  if (!OP_IDS.has(rule.op)) throw httpError(400, `Unknown operator: ${rule.op}`)

  if (EMAIL_OPS.has(rule.op)) {
    const check = ds?.emailChecks?.get(rule.column)
    if (!check || !check.done) {
      throw httpError(
        400,
        `Check the email domains in “${rule.column}” first — open the Emails tool and run the check.`,
      )
    }
    const wantValid = rule.op === 'email_valid'
    // Blank cells match neither operator: an absent email is not a broken one.
    // A domain the check never saw, or could not resolve, counts as valid —
    // "we could not check it" is no reason to drop a row.
    return (c) => {
      const v = c.get(col).trim()
      if (v === '') return false
      const domain = emailDomain(v)
      if (!domain) return !wantValid
      return (check.domains.get(domain)?.status !== 'bad') === wantValid
    }
  }

  const cs = !!rule.caseSensitive
  const norm = cs ? (s) => s : (s) => s.toLowerCase()
  const needle = norm(String(rule.value ?? ''))

  switch (rule.op) {
    case 'is_empty':
      return (c) => c.get(col) === ''
    case 'is_not_empty':
      return (c) => c.get(col) !== ''
    case 'contains':
      return (c) => norm(c.get(col)).includes(needle)
    case 'not_contains':
      return (c) => !norm(c.get(col)).includes(needle)
    case 'equals':
      return (c) => norm(c.get(col)) === needle
    case 'not_equals':
      return (c) => norm(c.get(col)) !== needle
    case 'starts_with':
      return (c) => norm(c.get(col)).startsWith(needle)
    case 'ends_with':
      return (c) => norm(c.get(col)).endsWith(needle)
    case 'in':
    case 'not_in': {
      const set = new Set((rule.values ?? []).map((v) => norm(String(v))))
      const want = rule.op === 'in'
      if (set.size === 0) return () => !want
      return (c) => set.has(norm(c.get(col))) === want
    }
    case 'regex': {
      let re
      try {
        re = new RegExp(String(rule.value ?? ''), cs ? '' : 'i')
      } catch (e) {
        throw httpError(400, `Invalid regex for ${rule.column}: ${e.message}`)
      }
      return (c) => re.test(c.get(col))
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const n = toNumber(String(rule.value ?? '').trim())
      if (Number.isNaN(n)) throw httpError(400, `${rule.column} ${rule.op} needs a number.`)
      if (rule.op === 'gt') return (c) => toNumber(c.get(col)) > n
      if (rule.op === 'gte') return (c) => toNumber(c.get(col)) >= n
      if (rule.op === 'lt') return (c) => toNumber(c.get(col)) < n
      return (c) => toNumber(c.get(col)) <= n
    }
    case 'between': {
      const lo = toNumber(String(rule.value ?? '').trim())
      const hi = toNumber(String(rule.value2 ?? '').trim())
      if (Number.isNaN(lo) || Number.isNaN(hi)) throw httpError(400, `${rule.column} between needs two numbers.`)
      const [a, b] = lo <= hi ? [lo, hi] : [hi, lo]
      return (c) => {
        const v = toNumber(c.get(col))
        return v >= a && v <= b
      }
    }
    default:
      throw httpError(400, `Unsupported operator: ${rule.op}`)
  }
}

/** Compile a pruned node — group or rule — into a single predicate. */
function compileNode(node, headers, ds) {
  if (!isGroup(node)) return compileRule(node, headers, ds)
  const tests = node.rules.map((child) => compileNode(child, headers, ds))
  if (combinatorOf(node) === 'OR') {
    return (c) => {
      for (let i = 0; i < tests.length; i++) if (tests[i](c)) return true
      return false
    }
  }
  return (c) => {
    for (let i = 0; i < tests.length; i++) if (!tests[i](c)) return false
    return true
  }
}

/**
 * Build a predicate over a RecordCursor. `ds` is only consulted by the email
 * operators, which read that dataset's finished domain check.
 * @returns {null | ((cursor) => boolean)} null means "match everything".
 */
export function compileFilter(spec, headers, ds = null) {
  const pruned = pruneSpec(spec)
  if (!pruned) return null
  return compileNode(pruned, headers, ds)
}
